/**
 * グラフデータから「テキスト要約 + コンパクトな表」を生成（生成AI用の第2層）
 */

const WEEKDAY_JA = ['日', '月', '火', '水', '木', '金', '土'];
const WEEKDAY_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const DAY_ORDER = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** @typedef {{ title: string, markdownTable: string }} DataTableRow */

/** @typedef {{ textualSummary: string, dataTables: DataTableRow[], hasPrediction?: boolean, unsupportedReason?: string }} DecisionSummary */

export const UNSUPPORTED_AI_PROMPT_ACTIONS = new Set([
  'today_details',
  'event_effect',
  'foreigners_distribution',
  'foreigners_yearly_distribution',
]);

export function isAiPromptSupported(action) {
  return Boolean(action && !UNSUPPORTED_AI_PROMPT_ACTIONS.has(action));
}

function round1(n) {
  return Math.round(n * 10) / 10;
}

function mean(arr) {
  if (!arr.length) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

function stdDev(arr) {
  if (arr.length < 2) return 0;
  const m = mean(arr);
  const v = arr.reduce((s, x) => s + (x - m) ** 2, 0) / (arr.length - 1);
  return Math.sqrt(v);
}

function fmtTable(headers, rows) {
  const esc = (c) => String(c).replace(/\|/g, '\\|');
  const head = `| ${headers.map(esc).join(' | ')} |`;
  const sep = `| ${headers.map(() => '---').join(' | ')} |`;
  const body = rows.map((r) => `| ${r.map(esc).join(' | ')} |`).join('\n');
  return `${head}\n${sep}\n${body}`;
}

/**
 * カレンダー格子データから日別リストへ
 * @param {unknown[][]} calendarData
 */
export function flattenCalendarCells(calendarData, year, month) {
  if (!Array.isArray(calendarData)) return [];
  const y = parseInt(String(year), 10);
  const m = parseInt(String(month), 10);
  const out = [];
  for (const week of calendarData) {
    if (!Array.isArray(week)) continue;
    for (const cell of week) {
      if (!cell || cell.date == null) continue;
      const congestion = Number(cell.congestion);
      if (Number.isNaN(congestion)) continue;
      const d = parseInt(String(cell.date), 10);
      if (Number.isNaN(d)) continue;
      const dateObj = new Date(y, m - 1, d);
      const iso = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      out.push({
        dateStr: iso,
        dayOfWeek: dateObj.getDay(),
        congestion,
        weather: cell.weather_info?.weather,
        isPrediction: cell.source === 'prediction',
      });
    }
  }
  return out;
}

function summarizeCalendarDays(days, eventData, lang, hasPredictionMix) {
  const labels = lang === 'en' ? WEEKDAY_EN : WEEKDAY_JA;
  const buckets = [[], [], [], [], [], [], []];
  const bucketsActual = [[], [], [], [], [], [], []];
  for (const d of days) {
    buckets[d.dayOfWeek].push(d.congestion);
    if (!d.isPrediction) {
      bucketsActual[d.dayOfWeek].push(d.congestion);
    }
  }

  const stats = [];
  for (let dow = 0; dow < 7; dow += 1) {
    const vals = buckets[dow];
    if (!vals.length) continue;
    const predictionCount = days.filter((x) => x.dayOfWeek === dow && x.isPrediction).length;
    stats.push({
      dow,
      label: labels[dow],
      avg: mean(vals),
      max: Math.max(...vals),
      min: Math.min(...vals),
      n: vals.length,
      predictionCount,
      sd: stdDev(vals),
    });
  }

  /** 実績日のみの曜日別集計（予測混在月の比較用） */
  const statsActual = [];
  for (let dow = 0; dow < 7; dow += 1) {
    const vals = bucketsActual[dow];
    if (!vals.length) continue;
    statsActual.push({
      dow,
      label: labels[dow],
      avg: mean(vals),
      max: Math.max(...vals),
      min: Math.min(...vals),
      n: vals.length,
      sd: stdDev(vals),
    });
  }

  stats.sort((a, b) => a.avg - b.avg);
  const lowest = stats[0];
  const highest = stats[stats.length - 1];

  const sortedByCongestion = [...days].sort((a, b) => b.congestion - a.congestion);
  const topDays = sortedByCongestion.slice(0, Math.min(8, sortedByCongestion.length));

  const eventDates = new Set((eventData || []).map((e) => e.date));

  const nPred = days.filter((d) => d.isPrediction).length;
  const nAct = days.length - nPred;

  const bullets = [];
  if (hasPredictionMix) {
    if (lang === 'ja') {
      bullets.push(
        `この月の日次には、すでに通りを計測した日が${nAct}日、先の日の見込み（アプリでは予測）が${nPred}日含まれます。曜日別の平均・最大・最小は、同じ曜日の中でも計測済みと見込みを混ぜて出しています（表の「うち実績／うち予測」列）。根拠に数値を使うときは日別表の備考で、計測済みか見込みかを必ず区別してください。`,
      );
    } else {
      bullets.push(
        `This month mixes ${nAct} actual day(s) and ${nPred} forecast day(s). Weekday averages/max/min combine both within the same weekday (see “actual / forecast day counts” columns). When citing numbers, always label each date as actual vs forecast using the day table’s Notes column.`,
      );
    }
  }

  const mixNoteJa = hasPredictionMix ? '（※曜日別の平均には、見込みの日の値も混ざることがあります）' : '';
  const mixNoteEn = hasPredictionMix ? ' (avg mixes actual + forecast within the weekday)' : '';
  const allActualJa = hasPredictionMix ? '' : '、該当する日はすべて計測済み';
  const allActualEn = hasPredictionMix ? '' : ', all measured days (no forecast rows)';

  if (lowest && highest && lang === 'ja') {
    bullets.push(
      `${lowest.label}曜の平均混雑度が最も低い（平均${round1(lowest.avg)}、${lowest.n}日分のデータ${allActualJa}）${mixNoteJa}。`,
      `${highest.label}曜の平均が最も高い（平均${round1(highest.avg)}、${highest.n}日分のデータ${allActualJa}）${mixNoteJa}。`,
    );
    const volatile = stats.filter((s) => s.sd >= 1.5 && s.n >= 3).sort((a, b) => b.sd - a.sd)[0];
    if (volatile) {
      bullets.push(`${volatile.label}曜は、曜日のなかでも日によって混み具合の差が大きい（ばらつきの目安はおおよそ${round1(volatile.sd)}${allActualJa}）${mixNoteJa}。`);
    }
  } else if (lowest && highest) {
    bullets.push(
      `${lowest.label} has the lowest average congestion (${round1(lowest.avg)}, n=${lowest.n} days${allActualEn})${mixNoteEn}.`,
      `${highest.label} has the highest average (${round1(highest.avg)}, n=${highest.n} days${allActualEn})${mixNoteEn}.`,
    );
    const volatile = stats.filter((s) => s.sd >= 1.5 && s.n >= 3).sort((a, b) => b.sd - a.sd)[0];
    if (volatile) {
      bullets.push(`${volatile.label} shows relatively high day-to-day variability (σ≈${round1(volatile.sd)}${allActualEn})${mixNoteEn}.`);
    }
  }

  if (eventDates.size && lang === 'ja') {
    bullets.push(`登録イベントのある日付は ${eventDates.size} 日分あり、混雑とイベントの重なりを確認してください。`);
  } else if (eventDates.size) {
    bullets.push(`${eventDates.size} calendar day(s) have listed events; cross-check peaks against events.`);
  }

  const header = lang === 'en'
    ? ['Weekday', 'Avg', 'Max', 'Min', 'Days', 'actual days', 'forecast days']
    : ['曜日', '平均混雑度', '最大', '最小', '日数', 'うち実績', 'うち予測'];
  const rows = [...stats].sort((a, b) => a.dow - b.dow).map((s) => [
    s.label,
    round1(s.avg),
    s.max,
    s.min,
    s.n,
    s.n - s.predictionCount,
    s.predictionCount,
  ]);

  const table1 = rows.length ? fmtTable(header, rows) : '';

  const headerActual = lang === 'en'
    ? ['Weekday', 'Avg (actual only)', 'Max', 'Min', 'days']
    : ['曜日', '平均（実績のみ）', '最大', '最小', '実績日数'];
  const rowsActual = [...statsActual].sort((a, b) => a.dow - b.dow).map((s) => [
    s.label,
    round1(s.avg),
    s.max,
    s.min,
    s.n,
  ]);
  const tableActual = hasPredictionMix && rowsActual.length ? fmtTable(headerActual, rowsActual) : '';

  const topHeader = lang === 'en'
    ? ['Date', 'Congestion', 'Notes']
    : ['日付', '混雑度', '備考'];
  const topRows = topDays.map((d) => {
    const kind = d.isPrediction
      ? (lang === 'en' ? 'forecast' : '予測')
      : (lang === 'en' ? 'actual' : '実績');
    const note = [
      eventDates.has(d.dateStr) ? (lang === 'en' ? 'event day' : 'イベント登録日') : '',
      kind,
    ].filter(Boolean).join(', ');
    return [d.dateStr, d.congestion, note || '—'];
  });
  const table2 = topRows.length ? fmtTable(topHeader, topRows) : '';

  /** @type {DataTableRow[]} */
  const dataTables = [];
  if (table1) {
    dataTables.push({
      title: lang === 'en'
        ? (hasPredictionMix
          ? 'Weekday aggregates (this month; avg mixes actual + forecast — see counts)'
          : 'Weekday aggregates (this month)')
        : (hasPredictionMix
          ? '曜日別の平均・最大・最小混雑度（実績＋予測を同一曜日内で混合）'
          : '曜日別の平均・最大・最小混雑度'),
      markdownTable: table1,
    });
  }
  if (tableActual) {
    dataTables.push({
      title: lang === 'en'
        ? 'Weekday aggregates (actual days only — for comparison)'
        : '曜日別の平均・最大・最小（実績日のみ・比較用）',
      markdownTable: tableActual,
    });
  }
  if (table2) {
    dataTables.push({
      title: lang === 'en' ? 'Highest congestion days (top)' : '混雑度が高かった日（上位）',
      markdownTable: table2,
    });
  }

  return {
    textualSummary: bullets.map((b) => `- ${b}`).join('\n'),
    dataTables,
    hasPrediction: hasPredictionMix,
  };
}

function summarizeWti(calendarData, lang) {
  if (!Array.isArray(calendarData) || !calendarData.length) {
    return { textualSummary: '', dataTables: [] };
  }
  const first = calendarData[0];
  if (!first?.day || !Array.isArray(first.hours)) {
    return { textualSummary: '', dataTables: [] };
  }

  const sorted = [...calendarData].sort(
    (a, b) => DAY_ORDER.indexOf(a.day) - DAY_ORDER.indexOf(b.day),
  );

  const hourBucketsWeekday = {};
  const hourBucketsWeekend = {};

  const isWeekend = (dayName) => dayName === 'Saturday' || dayName === 'Sunday';

  for (const row of sorted) {
    const weekend = isWeekend(row.day);
    const bucketSet = weekend ? hourBucketsWeekend : hourBucketsWeekday;
    for (const h of row.hours || []) {
      const hr = h.hour;
      const c = Number(h.congestion);
      if (Number.isNaN(hr) || Number.isNaN(c)) continue;
      if (!bucketSet[hr]) bucketSet[hr] = [];
      bucketSet[hr].push(c);
    }
  }

  const hours = Array.from({ length: 16 }, (_, i) => i + 7);
  const rows = hours.map((hr) => {
    const wd = hourBucketsWeekday[hr];
    const we = hourBucketsWeekend[hr];
    return [
      `${hr}:00`,
      wd?.length ? round1(mean(wd)) : '—',
      we?.length ? round1(mean(we)) : '—',
    ];
  });

  const header = lang === 'en'
    ? ['Hour', 'Weekday avg', 'Weekend avg']
    : ['時間帯', '平日平均', '休日平均'];
  const table = fmtTable(header, rows);

  let peakHr = 7;
  let peakAvg = -1;
  for (const hr of hours) {
    const vals = [...(hourBucketsWeekday[hr] || []), ...(hourBucketsWeekend[hr] || [])];
    if (!vals.length) continue;
    const a = mean(vals);
    if (a > peakAvg) {
      peakAvg = a;
      peakHr = hr;
    }
  }

  const bullets = lang === 'en'
    ? [
      `Peak hour band centers around ${peakHr}:00 (avg≈${round1(peakAvg)} across included weekdays/weekends).`,
      'Weekend averages are often higher than weekday averages in daytime hours.',
    ]
    : [
      `昼夜のピークはおおよそ ${peakHr}時台付近（全日・平日休日合算の平均 約${round1(peakAvg)}）。`,
      '昼間帯は休日平均が平日平均を上回りやすい傾向があることが多い。',
    ];

  return {
    textualSummary: bullets.map((b) => `- ${b}`).join('\n'),
    dataTables: [{ title: lang === 'en' ? 'Hour × weekday/weekend averages' : '時間帯×平日/休日の平均混雑度', markdownTable: table }],
  };
}

function summarizeDti(calendarData, lang) {
  if (!Array.isArray(calendarData) || !calendarData.length) {
    return { textualSummary: '', dataTables: [] };
  }
  const first = calendarData[0];
  if (!first?.date || !Array.isArray(first.hours)) {
    return { textualSummary: '', dataTables: [] };
  }

  const hourBucketsWeekday = {};
  const hourBucketsWeekend = {};

  for (const row of calendarData) {
    const dow = new Date(`${row.date}T12:00:00`).getDay();
    const weekend = dow === 0 || dow === 6;
    const bucketSet = weekend ? hourBucketsWeekend : hourBucketsWeekday;
    for (const h of row.hours || []) {
      const hr = h.hour;
      const c = Number(h.congestion);
      if (Number.isNaN(hr) || Number.isNaN(c)) continue;
      if (!bucketSet[hr]) bucketSet[hr] = [];
      bucketSet[hr].push(c);
    }
  }

  const hours = Array.from({ length: 16 }, (_, i) => i + 7);
  const rows = hours.map((hr) => {
    const wd = hourBucketsWeekday[hr];
    const we = hourBucketsWeekend[hr];
    return [
      `${hr}:00`,
      wd?.length ? round1(mean(wd)) : '—',
      we?.length ? round1(mean(we)) : '—',
    ];
  });

  const header = lang === 'en'
    ? ['Hour', 'Weekday avg', 'Weekend avg']
    : ['時間帯', '平日平均', '休日平均'];
  const table = fmtTable(header, rows);

  const bullets = lang === 'en'
    ? [
      'Aggregated from date×hour rows; weekend/holiday combines Sat/Sun.',
      'Compare weekday vs weekend daytime peaks when planning hours or staffing.',
    ]
    : [
      '日付×時間のデータを、平日と休日（土日）に分けて時間帯平均した集計です。',
      '開店・閉店やシフトは、平日と休日でピーク時間がずれるかを重点的に見てください。',
    ];

  return {
    textualSummary: bullets.map((b) => `- ${b}`).join('\n'),
    dataTables: [{ title: lang === 'en' ? 'Hour × weekday/weekend (date×time view)' : '時間帯×平日/休日（日付×時間ビュー由来）', markdownTable: table }],
  };
}

function summarizeTrend(data, action, lang) {
  if (!Array.isArray(data) || !data.length) {
    return { textualSummary: '', dataTables: [] };
  }

  /** @type {{ sortKey: number, label: string, c: number }[]} */
  const points = [];

  for (const item of data) {
    const c = Number(item.congestion);
    if (Number.isNaN(c)) continue;
    if (action === 'year_trend') {
      const y = Number(item.year);
      if (Number.isNaN(y)) continue;
      points.push({ sortKey: y, label: String(y), c });
    } else if (action === 'month_trend') {
      const y = Number(item.year);
      const m = Number(item.month);
      if (Number.isNaN(y) || Number.isNaN(m)) continue;
      points.push({
        sortKey: y * 100 + m,
        label: `${y}-${String(m).padStart(2, '0')}`,
        c,
      });
    } else {
      const y = Number(item.year);
      const m = Number(item.month);
      const w = Number(item.week ?? 1);
      if (Number.isNaN(y) || Number.isNaN(m)) continue;
      const label = item.start_date
        ? String(item.start_date)
        : `${y}-${String(m).padStart(2, '0')} W${w}`;
      const sk = item.start_date
        ? new Date(item.start_date).getTime()
        : y * 10000 + m * 100 + w;
      points.push({ sortKey: sk, label, c });
    }
  }

  if (!points.length) {
    return { textualSummary: '', dataTables: [] };
  }

  points.sort((a, b) => a.sortKey - b.sortKey);

  const minP = points.reduce((a, b) => (a.c <= b.c ? a : b));
  const maxP = points.reduce((a, b) => (a.c >= b.c ? a : b));

  const bullets = lang === 'en'
    ? [
      `Lowest congestion bucket: ${minP.label} (value ${round1(minP.c)}).`,
      `Highest congestion bucket: ${maxP.label} (value ${round1(maxP.c)}).`,
    ]
    : [
      `混雑度が最も低い区分: ${minP.label}（値 ${round1(minP.c)}）。`,
      `混雑度が最も高い区分: ${maxP.label}（値 ${round1(maxP.c)}）。`,
    ];

  const recent = points.slice(-12);
  const header = lang === 'en' ? ['Bucket', 'Congestion'] : ['区分', '混雑度'];
  const rows = recent.map((p) => [p.label, round1(p.c)]);
  const table = rows.length ? fmtTable(header, rows) : '';

  return {
    textualSummary: bullets.map((b) => `- ${b}`).join('\n'),
    dataTables: table
      ? [{
        title: lang === 'en' ? 'Recent buckets (last up to 12 points)' : '直近の推移（最大12点）',
        markdownTable: table,
      }]
      : [],
  };
}

/**
 * @param {{
 *   action: string,
 *   calendarData: unknown,
 *   data: unknown,
 *   eventData: Array<{date: string, title: string}>,
 *   year: string|number,
 *   month: string|number,
 *   lang: 'ja'|'en',
 * }} params
 * @returns {DecisionSummary}
 */
export function generateDecisionSummary(params) {
  const {
    action, calendarData, data, eventData, year, month, lang,
  } = params;

  if (!isAiPromptSupported(action)) {
    return {
      textualSummary: '',
      dataTables: [],
      unsupportedReason: lang === 'en'
        ? 'This view is not supported for AI prompt export yet.'
        : 'この表示ではまだ生成AI用プロンプトを作成していません。',
    };
  }

  const hasPredictionMix = Array.isArray(calendarData)
    && calendarData.some((row) => {
      if (Array.isArray(row)) {
        return row.some((cell) => cell?.source === 'prediction');
      }
      if (row?.hours) {
        return row.hours.some((h) => h?.source === 'prediction');
      }
      return row?.source === 'prediction';
    });

  if (action.startsWith('cal_')) {
    const days = flattenCalendarCells(calendarData, year, month);
    if (!days.length) {
      return {
        textualSummary: lang === 'en' ? '- No day-level congestion rows parsed.' : '- 日別の混雑データを読み取れませんでした。',
        dataTables: [],
        hasPrediction: hasPredictionMix,
      };
    }
    return summarizeCalendarDays(days, eventData, lang, hasPredictionMix);
  }

  if (action.startsWith('wti_')) {
    return { ...summarizeWti(calendarData, lang), hasPrediction: hasPredictionMix };
  }

  if (action.startsWith('dti_')) {
    return { ...summarizeDti(calendarData, lang), hasPrediction: hasPredictionMix };
  }

  if (action === 'year_trend' || action === 'month_trend' || action === 'week_trend') {
    return { ...summarizeTrend(data, action, lang), hasPrediction: hasPredictionMix };
  }

  return {
    textualSummary: '',
    dataTables: [],
    unsupportedReason: lang === 'en' ? 'Unsupported action.' : '未対応のアクションです。',
  };
}
